// useDiagramm.ts — eine ECharts-Instanz an einem div, nach den Diagramm-Hausregeln (Spec S19).
import { useCallback, useEffect, useRef, useState } from "react";
import { ladeEcharts } from "./echartsLaden.js";
import { diagrammThema, hausregelTooltip } from "./diagrammThema.js";
/** Gesten ohne Mausrad (Hausregel 14.10.2026): Rad scrollt die Seite, Ziehen und Pinch zoomen bzw. blättern. */
const ZOOM_VORGABE = {
    type: "inside", xAxisIndex: 0, filterMode: "none",
    zoomOnMouseWheel: false, moveOnMouseWheel: false, moveOnMouseMove: true, preventDefaultMouseMove: false,
};
const istObjekt = (w) => typeof w === "object" && w !== null && !Array.isArray(w);
/** Legt `vorgabe` unter `wert`: Objekte eine Ebene tief zusammen, Listen Eintrag für Eintrag (ein einzelnes Objekt
 *  gegen eine Vorgabeliste zählt als Liste mit einem Eintrag — `dataZoom: {…}` bliebe sonst ein Schieber ohne die
 *  Vorgabe „inside“); sonst gewinnt `wert`. */
function unterlege(vorgabe, wert) {
    if (wert === undefined)
        return vorgabe;
    if (istObjekt(vorgabe) && istObjekt(wert))
        return { ...vorgabe, ...wert };
    if (Array.isArray(vorgabe) && (Array.isArray(wert) || istObjekt(wert))) {
        return (Array.isArray(wert) ? wert : [wert]).map((w, i) => unterlege(vorgabe[i], w));
    }
    return wert;
}
/** Hausregel-Vorgaben unter die Option: ohne Animation, Tokenfarben, Popup im Hausregel-Stil, bei einer x-Achse
 *  dataZoom inside ohne Mausrad. Gleichnamiges in der Option gewinnt. */
function mitVorgaben(option, thema) {
    const vorgaben = {
        animation: false, color: thema.color, textStyle: { color: thema.text }, tooltip: hausregelTooltip(),
        ...("xAxis" in option ? { dataZoom: [ZOOM_VORGABE] } : {}),
    };
    const ergebnis = { ...option };
    for (const [schluessel, vorgabe] of Object.entries(vorgaben))
        ergebnis[schluessel] = unterlege(vorgabe, option[schluessel]);
    return ergebnis;
}
/** Das Diagrammthema aus den Tokens, neu bei Änderung von `data-theme` oder `data-unternehmen` (`--aktion`) am documentElement (MutationObserver) und
 *  beim Wechsel des System-Farbschemas (Einstellung SYSTEM: data-theme fehlt, prefers-color-scheme entscheidet,
 *  applyTheme.ts). Für Verbraucher, die ihre Option im selben Render aus dem Thema bauen (`useMemo`). */
export function useDiagrammThema() {
    const [thema, setThema] = useState(() => diagrammThema());
    useEffect(() => {
        const neu = () => { setThema(diagrammThema()); };
        const beobachter = new MutationObserver(neu);
        beobachter.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "data-unternehmen"] });
        const system = typeof matchMedia === "function" ? matchMedia("(prefers-color-scheme: dark)") : null;
        system?.addEventListener("change", neu);
        return () => { beobachter.disconnect(); system?.removeEventListener("change", neu); };
    }, []);
    return thema;
}
/** `option === null` zeichnet nichts. `setOption(option, { notMerge: false, lazyUpdate: true })` bei jeder neuen
 *  Option; `resize()` per ResizeObserver; `dispose()` beim Aushängen; `thema` neu bei Änderung von `data-theme`
 *  oder `data-unternehmen` am documentElement und beim Wechsel des System-Farbschemas (useDiagrammThema); Hausregel-Vorgaben werden unter
 *  die Option gelegt (`animation: false`, `tooltip` aus hausregelTooltip(), `dataZoom inside` ohne Mausrad, wenn
 *  die Option `xAxis` hat; `dataZoom` als Objekt zählt wie eine Liste). Die Option bitte memoisieren — jede neue
 *  Identität setzt sie neu; wer sie aus dem Thema baut, nimmt dafür useDiagrammThema() im selben Render. */
export function useDiagramm(option, ereignisse) {
    const [knoten, setKnoten] = useState(null);
    const [instanz, setInstanz] = useState(null);
    const thema = useDiagrammThema();
    // Ereignisse über eine Referenz: eine neue Callback-Identität bindet die Instanz nicht neu.
    const ereignisRef = useRef(ereignisse);
    useEffect(() => { ereignisRef.current = ereignisse; });
    const ref = useCallback((k) => { setKnoten(k); }, []);
    useEffect(() => {
        if (knoten === null)
            return undefined;
        let aktiv = true;
        let inst = null;
        let beobachter = null;
        ladeEcharts().then((echarts) => {
            if (!aktiv)
                return;
            const i = echarts.init(knoten, undefined, { renderer: "canvas" });
            inst = i;
            i.on("click", (p) => { ereignisRef.current?.click?.(p); });
            i.on("mouseover", (p) => { ereignisRef.current?.mouseover?.(p); });
            i.on("mouseout", () => { ereignisRef.current?.mouseout?.(); });
            i.on("datazoom", () => {
                const zoom = i.getOption().dataZoom?.[0];
                if (typeof zoom?.startValue === "number" && typeof zoom.endValue === "number") {
                    ereignisRef.current?.datazoom?.({ startValue: zoom.startValue, endValue: zoom.endValue });
                }
            });
            if (typeof ResizeObserver === "function") {
                beobachter = new ResizeObserver(() => { i.resize(); });
                beobachter.observe(knoten);
            }
            setInstanz(i);
        }, () => {
            // Laden fehlgeschlagen: `bereit` bleibt false, die nächste Einhängung lädt neu (ladeEcharts hält nichts).
        });
        return () => {
            aktiv = false;
            beobachter?.disconnect();
            inst?.dispose();
            setInstanz(null);
        };
    }, [knoten]);
    useEffect(() => {
        if (instanz === null || option === null)
            return;
        instanz.setOption(mitVorgaben(option, thema), { notMerge: false, lazyUpdate: true });
    }, [instanz, option, thema]);
    return { ref, instanz, thema, bereit: instanz !== null };
}
//# sourceMappingURL=useDiagramm.js.map